import { PlayCircleOutlineOutlined } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListSubheader,
  Tooltip,
} from '@mui/material';
import { Permission } from '@procureai/permissions';
import { usePermissions } from '@procureai/permissions-ui';
import { WrapWith } from '@procureai/ui-mui';
import { useTranslation } from 'react-i18next';

import { Cover } from '~/components/Cover/Cover';

import { CaseReleaseListItem } from './components/CaseReleaseListItem/CaseReleaseListItem';
import { useDemandCasesBatchReleaseDialog } from './DemandCasesBatchReleaseDialog.hooks';

export interface DemandCasesBatchReleaseDialogProps {
  demandId?: string;
  open: boolean;
  onClose: () => void;
}

export const DemandCasesBatchReleaseDialog = ({ demandId, open, onClose }: DemandCasesBatchReleaseDialogProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'DemandCasesBatchReleaseDialog' });
  const { casesReleasable, casesNotReleasable, loading, isSelected, selectedTotal, onSelect, onRelease } =
    useDemandCasesBatchReleaseDialog({ demandId, open, onClose });

  const { hasPermission } = usePermissions();
  const canRelease = hasPermission(Permission.CaseRelease);

  return (
    <Dialog fullWidth maxWidth="md" open={open} onClose={onClose}>
      <DialogTitle>{t('title')}</DialogTitle>
      {loading ? null : (
        <DialogContent>
          {casesReleasable.length ? (
            <List
              subheader={
                <ListSubheader component="div" disableGutters={true}>
                  {t('subHeaderReady')}
                </ListSubheader>
              }
            >
              {casesReleasable.map((negotiationCase) => (
                <CaseReleaseListItem
                  key={negotiationCase.id}
                  item={negotiationCase}
                  selected={isSelected(negotiationCase.id)}
                  onSelect={onSelect}
                />
              ))}
            </List>
          ) : null}

          {casesNotReleasable.length ? (
            <List
              subheader={
                <ListSubheader component="div" disableGutters={true}>
                  {t('subHeaderNotReady')}
                </ListSubheader>
              }
            >
              {casesNotReleasable.map((negotiationCase) => (
                <CaseReleaseListItem
                  key={negotiationCase.id}
                  demandId={demandId}
                  item={negotiationCase}
                  selected={false}
                />
              ))}
            </List>
          ) : null}
        </DialogContent>
      )}

      <DialogActions>
        <Button variant="text" onClick={onClose}>
          {t('cancelLabel')}
        </Button>
        <WrapWith component={Tooltip} condition={!canRelease} title={t(`noPermission`)} placement="top">
          <span>
            <Button
              disabled={selectedTotal === 0 || !canRelease}
              variant="contained"
              color="success"
              endIcon={<PlayCircleOutlineOutlined />}
              onClick={onRelease}
            >
              {t('confirmLabel')}
            </Button>
          </span>
        </WrapWith>
      </DialogActions>
      {loading ? (
        <Cover>
          <CircularProgress />
        </Cover>
      ) : null}
    </Dialog>
  );
};
